/**
 * API · core.pinned_messages (Fase B·F2)
 *
 * Fijados a nivel canal (compartidos entre miembros). RLS:
 *  - SELECT si can_view_channel · INSERT/DELETE si is_channel_member.
 * `pinned_by` tiene DEFAULT auth.uid() en la tabla → no hace falta mandarlo.
 *
 * El cliente supabase ya apunta a schema `core` (db.schema='core'), igual que
 * el resto de la api de chat.
 */
import { supabase } from '@/lib/supabase';
import type { MessagesRow } from '@/types/database.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface PinnedMessageRow {
  id: string;
  channel_id: string;
  message_id: string;
  pinned_by: string;
  created_at: string;
}

export interface PinnedWithBody {
  pin: PinnedMessageRow;
  message: MessagesRow | null;
}

export async function listPins(channelId: string): Promise<PinnedMessageRow[]> {
  const { data, error } = await db
    .from('pinned_messages')
    .select('*')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as PinnedMessageRow[];
}

export async function pinMessage(channelId: string, messageId: string): Promise<void> {
  const { error } = await db
    .from('pinned_messages')
    .insert({ channel_id: channelId, message_id: messageId });
  // 23505 = unique_violation → ya estaba fijado, lo tratamos como éxito idempotente.
  if (error && error.code !== '23505') throw error;
}

export async function unpinMessage(channelId: string, messageId: string): Promise<void> {
  const { error } = await db
    .from('pinned_messages')
    .delete()
    .eq('channel_id', channelId)
    .eq('message_id', messageId);
  if (error) throw error;
}

/** Fijados del canal con el cuerpo del mensaje resuelto (panel "Fijados"). */
export async function listPinnedWithBodies(channelId: string): Promise<PinnedWithBody[]> {
  const pins = await listPins(channelId);
  if (pins.length === 0) return [];
  const ids = pins.map((p) => p.message_id);
  const { data: msgs, error } = await db.from('messages').select('*').in('id', ids);
  if (error) throw error;
  const byId = new Map<string, MessagesRow>(
    ((msgs ?? []) as MessagesRow[]).map((m) => [m.id, m]),
  );
  return pins.map((pin) => ({ pin, message: byId.get(pin.message_id) ?? null }));
}
